import type { RedesignPage, RedesignTask } from '@/types';

interface PageGridProps {
  pages: RedesignPage[];
  tasks: RedesignTask[];
  onSelectPage: (pageIndex: number) => void;
}

function getPageStatus(_page: RedesignPage, task: RedesignTask | undefined): { label: string; color: string; bg: string } {
  if (!task) return { label: 'Ready', color: '#1e40af', bg: '#dbeafe' };
  if (task.implementationIssue) return { label: 'Done', color: '#166534', bg: '#dcfce7' };
  if (task.status === 'failed') return { label: 'Failed', color: '#991b1b', bg: '#fee2e2' };
  if (task.status === 'redesigning') return { label: 'Processing', color: '#7c3aed', bg: '#ede9fe' };
  if (task.status === 'complete') {
    if (task.figmaDesign?.status === 'complete' && !task.designerIssue) return { label: 'Assign Designer', color: '#92400e', bg: '#fffbeb' };
    if (task.designerIssue && !task.implementationIssue) return { label: 'With Designer', color: '#7c3aed', bg: '#f3e8ff' };
    return { label: 'Review', color: '#0e7490', bg: '#cffafe' };
  }
  return { label: 'In Progress', color: '#4338ca', bg: '#e0e7ff' };
}

const VIEWPORT_ICONS: Record<string, string> = {
  mobile: 'phone_android',
  tablet: 'tablet',
  desktop: 'monitor',
};

export function PageGrid({ pages, tasks, onSelectPage }: PageGridProps) {
  const taskMap = new Map<string, RedesignTask>();
  for (const t of tasks) {
    taskMap.set(`${t.pageRef.route}|${t.pageRef.viewport}`, t);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
      {pages.map((page, idx) => {
        const task = taskMap.get(`${page.route}|${page.viewport}`);
        const status = getPageStatus(page, task);
        const viewportIcon = VIEWPORT_ICONS[page.viewport?.toLowerCase()] || 'devices';

        return (
          <div
            key={`${page.route}|${page.viewport}`}
            onClick={() => onSelectPage(idx)}
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              cursor: 'pointer',
              background: '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'all 200ms ease',
              transform: 'translateY(0)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(74, 64, 224, 0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {/* Thumbnail */}
            <div style={{ position: 'relative', height: 160, overflow: 'hidden', backgroundColor: '#f8fafc' }}>
              {page.originalScreenshotUrl ? (
                <img
                  src={page.originalScreenshotUrl}
                  alt={page.route}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#cbd5e1',
                    gap: 4,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 32 }}>hide_image</span>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    No screenshot
                  </span>
                </div>
              )}
              {/* Gradient overlay */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 48,
                  background: 'linear-gradient(to top, rgba(255,255,255,0.9) 0%, transparent 100%)',
                  pointerEvents: 'none',
                }}
              />
              {/* Status badge overlay */}
              <span
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  padding: '4px 10px',
                  borderRadius: 9999,
                  color: status.color,
                  background: status.bg,
                }}
              >
                {status.label}
              </span>
            </div>

            {/* Card body */}
            <div style={{ padding: 16 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: 4,
                }}
              >
                {page.route}
              </div>
              {page.title && (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    marginBottom: 12,
                  }}
                >
                  {page.title}
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: 12,
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{viewportIcon}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {page.viewport}
                  </span>
                </div>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#c7d2fe' }}>
                  arrow_forward
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
